<script setup>
import {
  computed,
  defineAsyncComponent,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import ConversationLingxingOrdersAPI from 'dashboard/api/conversationLingxingOrders';
import Button from 'dashboard/components-next/button/Button.vue';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactNotes from 'dashboard/routes/dashboard/conversation/contact/ContactNotes.vue';
import ContactInfo from 'dashboard/routes/dashboard/conversation/contact/ContactInfo.vue';
import ContactCountry from './ContactCountry.vue';
import EmailHistory from './EmailHistory.vue';
import OrderMatchedReviews from './OrderMatchedReviews.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { getDisplayTimeZone } from 'shared/helpers/timeHelper';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { storeNamePresentation, orderWorkPending, storeWorkPending } from 'dashboard/helper/storeDirectoryState.mjs';
import { orderFeedback, orderRequestFeedback } from 'dashboard/helper/agentFeedback.mjs';

// 售后跟进 card below the orders (Ant Design), loaded only once an order is linked.
const AfterSaleAddButton = defineAsyncComponent(() =>
  import('dashboard/routes/dashboard/afterSales/components/AfterSaleAddButton.vue')
);

const notesOpen = ref(true);

const props = defineProps({
  contactId: { type: [Number, String], default: null },
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { updateUISettings } = useUISettings();
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const countryContext = ref(null);
const contact = computed(() =>
  props.contactId ? store.getters['contacts/getContact'](props.contactId) : null
);
const hasData = ref(false);
const orders = ref([]);
const selectedIndex = ref(0);
const isLoading = ref(false);
const error = ref('');
let requestSequence = 0;
let pollTimer;
let pollCount = 0;
const canLookup = ref(false);
const orderNumber = ref('');
const lookupError = ref('');
const lookupNotice = ref('');
const isSubmitting = ref(false);
const isUnlinking = ref(false);
const unlinkError = ref('');
const confirmUnlink = ref(false);
const refreshingOrder = ref(null);
const refreshNotice = ref('');
const refreshError = ref('');
const isQuerying = computed(() => orderWorkPending(orders.value));
const isRefreshingStores = computed(() => storeWorkPending(orders.value));
const showLookup = computed(
  () => canLookup.value && (!orders.value.length || orders.value.some(order => order.sync_status !== 'synced' || order.lookup_pending))
);

const stopPolling = () => {
  clearTimeout(pollTimer);
  pollTimer = undefined;
};

const schedulePoll = () => {
  stopPolling();
  if (!isQuerying.value && !isRefreshingStores.value && !refreshingOrder.value?.accepted) return;
  if (pollCount >= 45) {
    if (refreshingOrder.value) {
      refreshError.value = '暂时无法确认订单更新结果，请稍后重新加载。';
      refreshingOrder.value = null;
    }
    lookupNotice.value = isQuerying.value ? '查询仍在处理中，请稍后点击重新加载。' : '店铺名称更新较慢，请稍后点击重新加载。';
    return;
  }
  pollTimer = setTimeout(() => {
    pollCount += 1;
    loadOrders(true, { force: !!refreshingOrder.value });
  }, 2000);
};

const lookupOrder = async () => {
  if (isUnlinking.value || isSubmitting.value || isQuerying.value || refreshingOrder.value) return;
  const value = orderNumber.value.trim();
  lookupError.value = '';
  lookupNotice.value = '';
  if (!/^[0-9]{3}-[0-9]{7}-[0-9]{7}$/.test(value)) {
    lookupError.value = '请输入正确的 Amazon 订单号，例如 123-1234567-1234567。';
    return;
  }
  const sequence = ++requestSequence;
  stopPolling();
  pollCount = 0;
  isSubmitting.value = true;
  try {
    const { data } = await ConversationLingxingOrdersAPI.lookup(props.conversationId, value);
    if (sequence !== requestSequence) return;
    const order = data.order;
    if (order) {
      orders.value = [order, ...orders.value.filter(item => item.amazon_order_id !== order.amazon_order_id)];
      selectedIndex.value = 0;
      hasData.value = true;
      ConversationLingxingOrdersAPI.remember(props.conversationId, { orders: orders.value, can_lookup: canLookup.value, country_context: countryContext.value });
      emitter.emit('borderchat:conversation-orders-changed', { conversationId: props.conversationId });
    }
    schedulePoll();
  } catch (requestError) {
    if (sequence !== requestSequence) return;
    lookupError.value = orderRequestFeedback('lookup', requestError?.response?.status);
  } finally {
    if (sequence === requestSequence) {
      isSubmitting.value = false;
      if (!lookupError.value) loadOrders(true);
    }
  }
};

const selectedOrder = computed(() => orders.value[selectedIndex.value]);
const selectedStore = computed(() => storeNamePresentation(selectedOrder.value));
// 售后跟进 card: rows of every linked order; new rows only for synced orders.
const afterSaleOrderIds = computed(() => orders.value.map(order => order.amazon_order_id).filter(Boolean));
const addableOrderIds = computed(() =>
  orders.value.filter(order => order.sync_status === 'synced').map(order => order.amazon_order_id)
);
const refreshSelectedOrder = async () => {
  if (!selectedOrder.value || refreshingOrder.value || isUnlinking.value || isSubmitting.value) return;
  refreshNotice.value = '';
  refreshError.value = '';
  stopPolling();
  pollCount = 0;
  const sequence = ++requestSequence;
  const selected = selectedOrder.value;
  const canRequestLookup = canLookup.value;
  refreshingOrder.value = {
    orderId: selected.amazon_order_id,
    previousSyncedAt: selected.last_synced_at,
    accepted: false,
  };
  const refresh = refreshingOrder.value;
  try {
    // Reuse the existing permission-checked, rate-limited lookup job. A GET
    // alone only reloads the local order cache and cannot refresh ERP facts.
    if (canRequestLookup) {
      await ConversationLingxingOrdersAPI.lookup(props.conversationId, selected.amazon_order_id);
      if (sequence !== requestSequence) return;
      refreshingOrder.value.accepted = true;
    }
    await loadOrders(true, { force: true });
    if (!canRequestLookup && refreshingOrder.value === refresh) {
      refreshingOrder.value = null;
      refreshNotice.value = '已重新加载已保存的订单信息；当前账号无法发起领星查询。';
    }
  } catch (requestError) {
    if (sequence !== requestSequence) return;
    refreshingOrder.value = null;
    refreshError.value = requestError?.response
      ? orderRequestFeedback('lookup', requestError.response.status)
      : '暂时无法确认订单更新结果，请稍后重新加载。';
  }
};
const unlinkOrder = async () => {
  const selected = selectedOrder.value;
  if (!selected?.can_unlink || isUnlinking.value || isSubmitting.value || refreshingOrder.value) return;
  const sequence = ++requestSequence;
  stopPolling();
  isUnlinking.value = true;
  unlinkError.value = '';
  try {
    await ConversationLingxingOrdersAPI.unlink(props.conversationId, selected.link_id);
    if (sequence !== requestSequence) return;
    orders.value = orders.value.filter(order => order.link_id !== selected.link_id);
    selectedIndex.value = 0;
    confirmUnlink.value = false;
    ConversationLingxingOrdersAPI.remember(props.conversationId, { orders: orders.value, can_lookup: canLookup.value, country_context: countryContext.value });
    emitter.emit('borderchat:conversation-orders-changed', { conversationId: props.conversationId });
  } catch (requestError) {
    if (sequence !== requestSequence) return;
    unlinkError.value = orderRequestFeedback('unlink', requestError?.response?.status);
  } finally {
    if (sequence === requestSequence) {
      isUnlinking.value = false;
      schedulePoll();
      if (!unlinkError.value) loadOrders(true);
    }
  }
};
watch(() => selectedOrder.value?.amazon_order_id, () => {
  confirmUnlink.value = false;
  unlinkError.value = '';
  refreshNotice.value = '';
  refreshError.value = '';
});


const close = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
    is_lingxing_sidebar_open: false,
  });
};

const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    dateStyle: 'short',
    timeStyle: 'medium',
    hour12: false,
    timeZone: getDisplayTimeZone(),
  }).format(date);
};

const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '—';
  try {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${amount} ${currency || ''}`.trim();
  }
};

const orderStatusLabel = status =>
  ({
    PendingAvailability: '等待供货',
    Pending: '待处理',
    Unshipped: '待发货',
    PartiallyShipped: '部分发货',
    Shipped: '已发货',
    InvoiceUnconfirmed: '发票待确认',
    Canceled: '已取消',
    Unfulfillable: '无法配送',
  })[status] ||
  status ||
  '—';

const syncStatus = status =>
  ({
    syncing: ['同步中', 'bg-n-blue-3 text-n-blue-11'],
    pending: ['等待重试', 'bg-n-amber-3 text-n-amber-11'],
    not_found: ['暂未找到', 'bg-n-amber-3 text-n-amber-11'],
    failed: ['同步失败', 'bg-n-ruby-3 text-n-ruby-11'],
  })[status] || ['等待同步', 'bg-n-alpha-2 text-n-slate-11'];

const loadOrders = async (quiet = false, { force = false } = {}) => {
  if (isUnlinking.value) return;
  requestSequence += 1;
  const sequence = requestSequence;
  if (!hasData.value) isLoading.value = true;
  error.value = '';
  try {
    const { data } = await ConversationLingxingOrdersAPI.getForConversation(
      props.conversationId, { force }
    );
    if (sequence !== requestSequence) return;
    const selectedId = selectedOrder.value?.amazon_order_id;
    hasData.value = true;
    countryContext.value = data.country_context || { source: 'unknown' };
    orders.value = data.orders || [];
    canLookup.value = data.can_lookup === true;
    selectedIndex.value = Math.max(0, orders.value.findIndex(order => order.amazon_order_id === selectedId));
    const refresh = refreshingOrder.value;
    if (refresh?.accepted) {
      const updated = orders.value.find(order => order.amazon_order_id === refresh.orderId);
      if (!updated) {
        refreshingOrder.value = null;
        refreshError.value = '订单关联已变化，请重新选择订单。';
      } else if (!updated.lookup_pending && updated.sync_status !== 'syncing') {
        if (updated.sync_status === 'synced' && updated.last_synced_at && updated.last_synced_at !== refresh.previousSyncedAt) {
          refreshingOrder.value = null;
          refreshNotice.value = `订单信息已更新 · ${formatDate(updated.last_synced_at)}`;
          emitter.emit('borderchat:conversation-orders-changed', { conversationId: props.conversationId });
        } else if (['failed', 'not_found'].includes(updated.sync_status)) {
          refreshingOrder.value = null;
          refreshError.value = orderFeedback(updated);
        }
      }
    }
    schedulePoll();
  } catch (requestError) {
    if (sequence !== requestSequence) return;
    if (refreshingOrder.value) {
      refreshingOrder.value = null;
      refreshError.value = '暂时无法确认订单更新结果，请稍后重新加载。';
      stopPolling();
    }
    if ([401, 403, 404].includes(requestError?.response?.status)) {
      orders.value = [];
      countryContext.value = null;
      canLookup.value = false;
      hasData.value = false;
    }
    error.value = hasData.value ? '更新失败，当前显示上次加载的数据，请稍后重试。' : orderRequestFeedback('load', requestError?.response?.status);
  } finally {
    if (sequence === requestSequence) isLoading.value = false;
  }
};

const latestIncoming = computed(() => {
  const chat = store.getters.getSelectedChat;
  return [chat?.id, chat?.messages?.filter(message => message.message_type === 0).at(-1)?.id];
});
watch(latestIncoming, (next, previous) => {
  if (!isSubmitting.value && !isUnlinking.value && !refreshingOrder.value && previous && next[0] === previous[0] && String(next[0]) === String(props.conversationId) && next[1] && next[1] !== previous[1]) {
    loadOrders(true);
  }
});

watch(() => [accountId.value, props.conversationId], () => {
  stopPolling();
  pollCount = 0;
  const cached = ConversationLingxingOrdersAPI.getCached(props.conversationId, { allowStale: true });
  hasData.value = !!cached;
  countryContext.value = cached?.country_context || null;
  orders.value = cached?.orders || [];
  canLookup.value = cached?.can_lookup === true;
  selectedIndex.value = 0;
  isLoading.value = false;
  orderNumber.value = '';
  lookupError.value = '';
  lookupNotice.value = '';
  isSubmitting.value = false;
  isUnlinking.value = false;
  confirmUnlink.value = false;
  unlinkError.value = '';
  refreshingOrder.value = null;
  refreshNotice.value = '';
  refreshError.value = '';
  loadOrders();
}, { immediate: true });

onBeforeUnmount(() => {
  requestSequence += 1;
  stopPolling();
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text vue/html-closing-bracket-newline -->
<template>
  <div class="flex h-full min-h-0 w-full flex-col">
    <header
      class="flex h-12 shrink-0 items-center justify-between border-b border-n-weak px-4"
    >
      <span class="text-sm font-medium text-n-slate-12">{{ $t('CONVERSATION.SIDEBAR.BASIC_INFO') }}</span>
      <Button icon="i-lucide-x" slate ghost sm @click="close" />
    </header>

    <div class="min-h-0 flex-1 overflow-y-auto p-3">
    <ContactInfo
      v-if="contact?.id"
      :key="`${accountId}:${contact.id}`"
      :contact="contact"
      class="mb-3 border-b border-n-weak"
      @panel-close="close"
    >
      <template #profile-extra>
        <ContactCountry
          :contact="contact"
          :country-context="countryContext"
          :loading="isLoading"
        />
      </template>
    </ContactInfo>
    <EmailHistory :key="`${accountId}:${conversationId}:history`" :conversation-id="conversationId" />
    <div v-if="isLoading" class="flex min-h-24 items-center justify-center">
      <Spinner class="text-n-brand" />
    </div>
    <div
      v-else-if="error && !hasData"
      class="m-4 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-11"
    >
      <p class="font-medium">订单信息加载失败</p>
      <p class="mt-1">{{ error }}</p>
      <Button
        class="mt-3"
        label="重新加载"
        ruby
        outline
        sm
        @click="loadOrders"
      />
    </div>
    <div v-else>
      <div v-if="error && hasData" role="status" class="mb-3 text-xs text-n-amber-11">
        {{ error }}
        <Button label="重试" slate ghost sm @click="loadOrders" />
      </div>
      <div v-if="!orders.length" class="px-3 py-3 text-center">
        <span class="i-lucide-shopping-bag inline-block size-7 text-n-slate-9" />
        <p class="mb-0 mt-2 text-sm font-medium text-n-slate-12">暂无订单信息</p>
        <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-10">
          输入 Amazon 订单号，从领星查询商品和订单详情。
        </p>
      </div>

      <form v-if="showLookup" class="mb-4 rounded-xl border border-n-weak bg-n-solid-2 p-3" @submit.prevent="lookupOrder">
        <label for="lingxing-order-number" class="block text-xs font-medium text-n-slate-12">Amazon 订单号</label>
        <input
          id="lingxing-order-number"
          v-model="orderNumber"
          type="text"
          autocomplete="off"
          maxlength="25"
          placeholder="123-1234567-1234567"
          class="mb-0 mt-2 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
          :disabled="isUnlinking || isSubmitting || isQuerying || !!refreshingOrder"
          :aria-invalid="!!lookupError"
          :aria-describedby="lookupError || lookupNotice || isQuerying ? 'lingxing-lookup-feedback' : undefined"
        />
        <Button type="submit" class="mt-3 w-full" :label="isSubmitting || isQuerying ? '正在查询…' : '查询订单'" :disabled="isUnlinking || isSubmitting || isQuerying || !!refreshingOrder || !orderNumber.trim()" />
        <p v-if="lookupError || lookupNotice || isQuerying" id="lingxing-lookup-feedback" aria-live="polite" class="mb-0 mt-2 text-xs leading-5" :class="lookupError ? 'text-n-ruby-11' : 'text-n-slate-10'">
          {{ lookupError || lookupNotice || (isQuerying ? '正在从领星获取订单信息…' : '') }}
        </p>
      </form>
      <div
        v-if="orders.length > 1"
        class="mb-3 flex gap-1 overflow-x-auto rounded-lg bg-n-alpha-2 p-1"
      >
        <button
          v-for="(order, index) in orders"
          :key="order.amazon_order_id"
          type="button"
          :disabled="!!refreshingOrder || isSubmitting || isUnlinking"
          class="shrink-0 rounded-md px-2.5 py-1.5 text-xs font-medium"
          :class="
            index === selectedIndex
              ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
              : 'text-n-slate-10'
          "
          @click="selectedIndex = index"
        >
          {{ order.amazon_order_id }}
        </button>
      </div>

      <section
        v-if="selectedOrder"
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
      >
        <div class="px-3 py-2">
          <div class="flex min-w-0 items-center gap-1">
            <p
              class="mb-0 min-w-0 flex-1 truncate text-xs font-semibold leading-5 text-n-blue-11"
              :title="selectedOrder.amazon_order_id"
            >
              {{ selectedOrder.amazon_order_id }}
            </p>
            <span
              class="shrink-0 whitespace-nowrap rounded-md bg-n-amber-3 px-1.5 py-0.5 text-xs font-medium text-n-amber-11"
            >
              {{ orderStatusLabel(selectedOrder.order_status) }}
            </span>
            <Button v-if="selectedOrder.can_unlink" class="shrink-0" label="解绑" ruby ghost xs :disabled="isUnlinking || isSubmitting || !!refreshingOrder" @click="confirmUnlink = true" />
            <Button class="shrink-0" icon="i-lucide-refresh-cw" slate ghost xs :title="refreshingOrder ? '正在重新获取订单…' : '重新加载'" :aria-label="refreshingOrder ? '正在重新获取订单…' : '重新加载'" :is-loading="!!refreshingOrder" :disabled="isUnlinking || isSubmitting || !!refreshingOrder" @click="refreshSelectedOrder" />
          </div>
          <p v-if="refreshingOrder || refreshNotice || refreshError" :role="refreshError ? 'alert' : 'status'" aria-live="polite" class="mb-0 mt-2 text-xs leading-5" :class="refreshError ? 'text-n-ruby-11' : 'text-n-slate-11'">
            {{ refreshError || (refreshingOrder ? '正在从领星重新获取订单信息…' : refreshNotice) }}
          </p>
          <div v-if="confirmUnlink && selectedOrder.can_unlink" class="mt-3 rounded-lg bg-n-alpha-1 p-3 text-xs">
            <p>确认解除此订单与当前会话的关联？订单和商品数据会保留。</p>
            <div class="mt-2 flex gap-2">
              <Button :label="isUnlinking ? '正在解绑…' : '确认解绑'" ruby sm :disabled="isUnlinking" @click="unlinkOrder" />
              <Button label="取消" slate ghost sm :disabled="isUnlinking" @click="confirmUnlink = false" />
            </div>
          </div>
          <p v-if="unlinkError" role="alert" class="mt-2 text-xs text-n-ruby-11">{{ unlinkError }}</p>
          <div
            v-if="selectedOrder.sync_status !== 'synced' || selectedOrder.lookup_pending"
            class="mt-1 flex flex-wrap gap-1.5"
          >
            <span
              class="rounded-md px-2 py-1 text-xs font-medium"
              :class="syncStatus(selectedOrder.lookup_pending ? 'syncing' : selectedOrder.sync_status)[1]"
            >
              {{ syncStatus(selectedOrder.lookup_pending ? 'syncing' : selectedOrder.sync_status)[0] }}
            </span>
          </div>
          <p
            v-if="selectedOrder.sync_error_message"
            class="mt-3 rounded-lg bg-n-alpha-2 px-3 py-2 text-xs leading-5 text-n-slate-11"
          >
            {{ orderFeedback(selectedOrder) }}
          </p>
        </div>

        <dl
          v-if="selectedOrder.sync_status === 'synced'"
          class="grid gap-2 border-t border-n-weak px-3 py-3 text-xs"
        >
          <div class="grid grid-cols-[84px_minmax(0,1fr)] gap-2">
            <dt class="text-n-slate-9">所属店铺</dt>
            <dd class="min-w-0 break-words font-medium">
              <span>{{ selectedStore.name }}</span>
              <span v-if="selectedStore.notice" class="block text-xs font-normal text-n-amber-11" role="status">{{ selectedStore.notice }}</span>
            </dd>
          </div>
          <div v-if="selectedOrder.sales_channel" class="grid grid-cols-[84px_minmax(0,1fr)] gap-2">
            <dt class="text-n-slate-9">销售站点</dt>
            <dd class="min-w-0 break-words font-medium">{{ selectedOrder.sales_channel }}</dd>
          </div>
          <div class="grid grid-cols-[84px_minmax(0,1fr)] gap-2">
            <dt class="text-n-slate-9">订购时间</dt>
            <dd class="break-words font-medium">
              {{ formatDate(selectedOrder.purchase_date_utc) }}
            </dd>
          </div>
          <div class="grid grid-cols-[84px_minmax(0,1fr)] gap-2">
            <dt class="text-n-slate-9">订购金额</dt>
            <dd class="break-words font-medium">
              {{
                formatMoney(
                  selectedOrder.order_total_amount,
                  selectedOrder.currency
                )
              }}
            </dd>
          </div>
        </dl>

        <div
          v-if="selectedOrder.sync_status === 'synced'"
          class="divide-y divide-n-weak border-t border-n-weak"
        >
          <p
            v-if="!selectedOrder.items?.length"
            class="px-3 py-8 text-center text-xs text-n-slate-9"
          >
            领星未返回商品明细
          </p>
          <article
            v-for="item in selectedOrder.items"
            :key="item.order_item_id"
            class="flex flex-wrap gap-3 p-3"
          >
            <div
              class="relative size-16 shrink-0 overflow-hidden rounded-lg border border-n-weak bg-n-alpha-1"
            >
              <img
                v-if="item.product_image_url"
                :src="item.product_image_url"
                :alt="item.product_name || item.title || ''"
                class="size-full object-cover"
              />
              <span
                v-else
                class="flex size-full items-center justify-center text-n-slate-9"
                ><span class="i-lucide-image-off size-5"
              /></span>
              <span
                class="absolute bottom-0 right-0 rounded-tl bg-n-solid-2 px-1.5 py-0.5 text-xs"
                >×{{ item.quantity_ordered || 0 }}</span
              >
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-n-slate-12">
                {{
                  item.product_name || item.title || item.asin || '未命名商品'
                }}
              </p>
              <p v-if="item.asin" class="mt-1 break-all text-xs text-n-slate-9">
                ASIN
                <span class="font-medium text-n-blue-11">{{ item.asin }}</span>
              </p>
              <p
                v-if="item.seller_sku"
                class="mt-1 break-all text-xs text-n-slate-9"
              >
                MSKU
                <span class="font-medium text-n-slate-12">{{
                  item.seller_sku
                }}</span>
              </p>
            </div>
            <div
              class="w-full rounded-lg bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-10"
            >
              <p>本地 SKU：{{ item.local_sku || '—' }}</p>
            </div>
          </article>
        </div>
        <OrderMatchedReviews
          :key="`${conversationId}:${selectedOrder.amazon_order_id}`"
          :reviews="selectedOrder.amazon_reviews || []"
        />
      </section>

      <section
        v-if="afterSaleOrderIds.length"
        class="mt-3 overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 px-3 py-2"
        data-test="after-sale-add"
      >
        <AfterSaleAddButton
          :key="`${conversationId}:${afterSaleOrderIds.join(',')}`"
          :account-id="accountId"
          :conversation-id="conversationId"
          :amazon-order-ids="afterSaleOrderIds"
          :addable-order-ids="addableOrderIds"
          :preferred-order-id="selectedOrder?.amazon_order_id || ''"
          :disabled="isUnlinking || isSubmitting"
        />
      </section>
    </div>
    <AccordionItem
      v-if="contactId"
      class="mt-3"
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
      :is-open="notesOpen"
      compact
      @toggle="notesOpen = !notesOpen"
    >
      <ContactNotes :key="`${accountId}:${contactId}`" :contact-id="contactId" />
    </AccordionItem>
    </div>
  </div>
</template>
